import { supabase } from '../auth/supabase-client';
import type { Json } from '../types/database.generated';
import type { CheckRhythm, PieceJointe, Tache, TacheEvenement } from './types';

function client() {
  if (!supabase) throw new Error('Supabase indisponible.');
  return supabase;
}

const BUCKET = 'alba';

export async function chargerTaches(workspaceId: string): Promise<Tache[]> {
  const { data, error } = await client()
    .from('tasks')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('position')
    .order('created_at');
  if (error) throw error;
  return data ?? [];
}

export async function chargerTache(tacheId: string): Promise<Tache | null> {
  const { data, error } = await client().from('tasks').select('*').eq('id', tacheId).maybeSingle();
  if (error) throw error;
  return data;
}

/** Date du prochain point, déduite du rythme choisi. */
export function prochainPoint(rythme: CheckRhythm, depuis: Date = new Date()): string | null {
  const jours = { none: 0, hebdo: 7, quinzaine: 14, mensuel: 30, evenement: 0 }[rythme];
  if (!jours) return null;
  const cible = new Date(depuis.getTime());
  cible.setDate(cible.getDate() + jours);
  return cible.toISOString();
}

export type NouvelleTache = {
  id: string;
  workspaceId: string;
  listId: string;
  title: string;
  position: number;
  createdBy: string | null;
  ownerMemberId?: string | null;
};

export async function creerTache(valeurs: NouvelleTache): Promise<Tache> {
  const { data, error } = await client()
    .from('tasks')
    .insert({
      // L'identifiant vient du navigateur : la ligne affichée tout de suite et
      // la ligne enregistrée sont la même, sans clignotement ni doublon quand
      // le temps réel renvoie l'insertion.
      id: valeurs.id,
      workspace_id: valeurs.workspaceId,
      list_id: valeurs.listId,
      title: valeurs.title,
      position: valeurs.position,
      created_by_member_id: valeurs.createdBy,
      requester_member_id: valeurs.createdBy,
      owner_member_id: valeurs.ownerMemberId ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export type ChampsTache = Partial<{
  title: string;
  description: string | null;
  list_id: string;
  status: Tache['status'];
  starred: boolean;
  owner_member_id: string | null;
  requester_member_id: string | null;
  due_date: string | null;
  check_rhythm: CheckRhythm;
  next_check_at: string | null;
  amount_cents: number | null;
  reference: string | null;
  checklist: Json;
}>;

export async function majTache(tacheId: string, valeurs: ChampsTache): Promise<Tache> {
  const { data, error } = await client()
    .from('tasks')
    .update(valeurs)
    .eq('id', tacheId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function supprimerTache(tacheId: string): Promise<void> {
  const { error } = await client().from('tasks').delete().eq('id', tacheId);
  if (error) throw error;
}

/** Réécrit l'ordre d'une colonne en un seul aller-retour. */
export async function reordonnerTaches(listeId: string, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await client().rpc('reorder_tasks', { p_list: listeId, p_ids: ids });
  if (error) throw error;
}

export async function chargerEvenements(tacheId: string): Promise<TacheEvenement[]> {
  const { data, error } = await client()
    .from('task_events')
    .select('*')
    .eq('task_id', tacheId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function publierCommentaire(
  tacheId: string,
  corps: string,
  mentions: string[] = [],
): Promise<void> {
  const { error } = await client().rpc('publier_commentaire', {
    p_task: tacheId,
    p_body: corps,
    p_mentions: mentions,
  });
  if (error) throw error;
}

export async function signalerProbleme(tacheId: string, note: string): Promise<void> {
  const { error } = await client().rpc('signaler_probleme', { p_task: tacheId, p_note: note });
  if (error) throw error;
}

export async function chargerPiecesJointes(tacheId: string): Promise<PieceJointe[]> {
  const { data, error } = await client()
    .from('attachments')
    .select('*')
    .eq('task_id', tacheId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/** Nom de fichier sûr : le chemin sert de clé de visibilité côté Storage. */
function nomSur(nom: string): string {
  const nettoye = nom
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-|-$/g, '');
  return nettoye.slice(-80) || 'fichier';
}

export async function deposerPieceJointe(
  tacheId: string,
  auteurMemberId: string | null,
  fichier: File,
): Promise<PieceJointe> {
  const chemin = `taches/${tacheId}/${crypto.randomUUID()}-${nomSur(fichier.name)}`;
  const { error: erreurDepot } = await client()
    .storage.from(BUCKET)
    .upload(chemin, fichier, fichier.type ? { contentType: fichier.type } : {});
  if (erreurDepot) throw erreurDepot;

  const { data, error } = await client()
    .from('attachments')
    .insert({
      task_id: tacheId,
      kind: fichier.type.startsWith('image/') ? 'photo' : 'document',
      storage_path: chemin,
      original_filename: fichier.name,
      mime_type: fichier.type || null,
      size_bytes: fichier.size,
      author_member_id: auteurMemberId,
    })
    .select('*')
    .single();
  if (error) throw error;

  await client().from('task_events').insert({
    task_id: tacheId,
    kind: 'piece',
    body: fichier.name,
    author_member_id: auteurMemberId,
  });

  return data;
}

/** Le dépôt est privé : chaque aperçu passe par une adresse signée. */
export async function urlSignee(chemin: string, secondes = 3600): Promise<string | null> {
  const { data, error } = await client().storage.from(BUCKET).createSignedUrl(chemin, secondes);
  if (error) return null;
  return data?.signedUrl ?? null;
}
